/**
 * AddressAdmin (OWN-2d, #120) — the merge / move-address power-tool.
 *
 * Two admin operations on the PROPERTY overlay (ESC stays read-only, DEC-22):
 *   • MERGE — fold a duplicate property into a keeper; its ownership timeline,
 *     native equipment, and jobs re-point to the keeper. The loser is SOFT-merged
 *     (merged_into set, row kept) so it's reversible + auditable — never a silent
 *     destructive delete ([[feedback_review_before_commit]]).
 *   • MOVE — re-parent a property to a different ESC customer/location.
 * Both show a PREVIEW (what moves) before Confirm.
 *
 * Honest scope: pegasus_property fills as transfers / NC-handoffs / cutover create
 * properties, so this is the machinery ready ahead of the data (build-now, DEC-25).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { GitMerge, ArrowRightLeft, AlertTriangle, Check, Undo2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { CustomerPicker, type PickedCustomer } from "@/components/CustomerPicker";
// PROPERTY-LINK-01: every unanswered "same house?" pair, newest arrival first.
import { useHouseReviewList } from "@/features/customer-360/houseHistory";
import { HouseLinkPrompt } from "@/features/customer-360/HouseLinkPrompt";

interface AdminProperty {
  id: string;
  address: string;
  city: string | null;
  esc_custno: string | null;
  esc_locno: string | null;
  origin: string;
}

function useAdminProperties() {
  return useQuery({
    queryKey: ["admin_properties"],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<AdminProperty[]> => {
      const { data, error } = await supabase
        .from("pegasus_property")
        .select("id,address,city,esc_custno,esc_locno,origin")
        .is("merged_into", null)
        .order("address", { ascending: true })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as AdminProperty[];
    },
  });
}

async function countOn(table: string, propertyId: string): Promise<number> {
  const { count } = await supabase
    .from(table)
    .select("*", { count: "exact", head: true })
    .eq("property_id", propertyId);
  return count ?? 0;
}

function useMergePreview(loserId: string | null) {
  return useQuery({
    queryKey: ["merge_preview", loserId],
    enabled: !!loserId,
    queryFn: async () => {
      const [own, equip, jobs] = await Promise.all([
        countOn("pegasus_property_ownership", loserId!),
        countOn("pegasus_equipment", loserId!),
        countOn("pegasus_job", loserId!),
      ]);
      return { own, equip, jobs };
    },
  });
}

const CHILD_TABLES = ["pegasus_property_ownership", "pegasus_equipment", "pegasus_job"] as const;

/** What a merge moved — captured BEFORE moving, so undo can put back exactly
 *  these rows (SETTINGS-AUDIT-01: the old undo only un-hid the duplicate and
 *  left the children moved, while promising "reversible"). */
export interface MergeReceipt {
  keeperId: string;
  loserId: string;
  moved: Record<string, string[]>; // table -> child row ids that were re-pointed
}

function useMergeProperties() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ keeperId, loserId }: { keeperId: string; loserId: string }): Promise<MergeReceipt> => {
      // 1. Capture EXACTLY which child rows are about to move (this makes undo real).
      const moved: Record<string, string[]> = {};
      for (const t of CHILD_TABLES) {
        const { data, error } = await supabase.from(t).select("id").eq("property_id", loserId);
        if (error) throw new Error(`Couldn't read ${t}: ${error.message} — nothing was changed.`);
        moved[t] = (data ?? []).map((r) => r.id as string);
      }
      // 2. Re-point, CHECKING every write — a half-merged address is worse than no merge.
      for (const t of CHILD_TABLES) {
        if (!moved[t].length) continue;
        const { error } = await supabase.from(t).update({ property_id: keeperId }).eq("property_id", loserId);
        if (error) throw new Error(`Moving ${t} failed: ${error.message}. Some records may have moved — use Undo, then tell Claude.`);
      }
      const { error: hideErr } = await supabase.from("pegasus_property").update({ merged_into: keeperId }).eq("id", loserId);
      if (hideErr) throw new Error(`Children moved but hiding the duplicate failed: ${hideErr.message}. Use Undo to restore, then retry.`);
      return { keeperId, loserId, moved };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin_properties"] });
      qc.invalidateQueries({ queryKey: ["merge_preview"] });
    },
  });
}

function useUndoMerge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (receipt: MergeReceipt) => {
      // Put back EXACTLY the rows the merge moved (from the receipt), then
      // un-hide the duplicate. This is a true undo.
      for (const t of CHILD_TABLES) {
        const ids = receipt.moved[t] ?? [];
        if (!ids.length) continue;
        const { error } = await supabase.from(t).update({ property_id: receipt.loserId }).in("id", ids);
        if (error) throw new Error(`Restoring ${t} failed: ${error.message}`);
      }
      const { error } = await supabase.from("pegasus_property").update({ merged_into: null }).eq("id", receipt.loserId);
      if (error) throw new Error(`Un-hiding the address failed: ${error.message}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin_properties"] });
      qc.invalidateQueries({ queryKey: ["merge_preview"] });
    },
  });
}

function useMoveProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, custno, locno }: { id: string; custno: string; locno: string | null }) => {
      const { error } = await supabase
        .from("pegasus_property")
        .update({ esc_custno: custno.trim(), esc_locno: locno?.trim() || null })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin_properties"] }),
  });
}

const label = (p: AdminProperty) =>
  `${p.address}${p.city ? `, ${p.city}` : ""} — ${p.esc_custno ?? "?"}${p.esc_locno ? `/${p.esc_locno}` : ""}`;

export function AddressAdminSection() {
  const canManage = useCan("customers.merge");
  const { data: props = [], isLoading } = useAdminProperties();
  const [mode, setMode] = useState<"merge" | "move">("merge");

  return (
    <div className="space-y-4">
      <HouseReviewSection />
      <div className="rounded-xl border border-hairline bg-white p-5 shadow-e1">
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-hairline p-0.5">
            <button type="button" onClick={() => setMode("merge")}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${mode === "merge" ? "bg-brand-50 text-brand-800" : "text-slate-500 hover:bg-slate-50"}`}>
              <GitMerge className="h-3.5 w-3.5" /> Merge duplicates
            </button>
            <button type="button" onClick={() => setMode("move")}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${mode === "move" ? "bg-brand-50 text-brand-800" : "text-slate-500 hover:bg-slate-50"}`}>
              <ArrowRightLeft className="h-3.5 w-3.5" /> Move to customer
            </button>
          </div>
          <InfoTip label="About merging and moving">
            The home's equipment, ownership history and jobs travel with it. Only Pegasus records change; ESC stays untouched.
          </InfoTip>
        </div>

        {!canManage && (
          <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
            Read-only — merging or moving addresses needs the "Manage app settings" capability.
          </p>
        )}

        {isLoading ? (
          <p className="mt-4 text-sm text-slate-400">Loading properties…</p>
        ) : props.length === 0 ? (
          <div className="mt-4 rounded-lg border border-dashed border-slate-200 bg-slate-50/60 p-6 text-center">
            <p className="inline-flex items-center gap-1.5 text-sm text-slate-500">
              No properties recorded yet.
              <InfoTip label="Where properties come from">
                Properties are created when a home changes owners, a new-construction job hands off, or at the ESC cutover.
              </InfoTip>
            </p>
          </div>
        ) : mode === "merge" ? (
          <MergePanel props={props} disabled={!canManage} />
        ) : (
          <MovePanel props={props} disabled={!canManage} />
        )}
      </div>
    </div>
  );
}

/** PROPERTY-LINK-01: the review list — one ESC address, two accounts, one of
 *  them with history the other can't see. Answered pairs drop off; the Today
 *  page carries the last 45 days of arrivals, this carries everything. */
function HouseReviewSection() {
  const { data, isLoading } = useHouseReviewList(null, 1000);
  const rows = data ?? [];
  const [shown, setShown] = useState(25);
  return (
    <div className="rounded-xl border border-hairline bg-white p-5 shadow-e1">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-slate-900">One house, two accounts</h3>
        <span className="text-xs text-slate-400">{isLoading ? "counting…" : `${rows.length} to answer`}</span>
        <InfoTip label="What this is">
          ESC keeps one customer per party, so a house that changed hands — a builder then the homeowner, a seller then the buyer — carries two accounts, and each sees only its own visits and units. Pegasus pairs them by address (house number, zip, street, unit) and shows the history on both the 360 and the tech&apos;s iPad. Answering here makes the pair a recorded fact (or tells Pegasus to stop pairing them). Nothing in ESC changes; nothing moves between accounts.
        </InfoTip>
      </div>
      {isLoading ? (
        <p className="mt-3 text-sm text-slate-400">Reading the address book…</p>
      ) : rows.length === 0 ? (
        <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-slate-500"><Check className="h-4 w-4 text-emerald-600" /> Nothing waiting — every known pair has been answered.</p>
      ) : (
        <>
          <div className="mt-3 space-y-2">
            {rows.slice(0, shown).map((r) => (
              <HouseLinkPrompt key={`${r.custno}/${r.locno ?? ""}→${r.other_custno}`} row={r} />
            ))}
          </div>
          {rows.length > shown && (
            <button type="button" onClick={() => setShown((n) => n + 25)} className="mt-3 block w-full rounded-md py-2 text-center text-xs font-medium text-brand-600 transition-colors hover:bg-brand-50/40 hover:text-brand-700">
              Show {Math.min(25, rows.length - shown)} more ({rows.length - shown} left)
            </button>
          )}
        </>
      )}
    </div>
  );
}

function MergePanel({ props, disabled }: { props: AdminProperty[]; disabled: boolean }) {
  const [keeperId, setKeeperId] = useState("");
  const [loserId, setLoserId] = useState("");
  const [confirming, setConfirming] = useState(false);
  // The receipt of what actually moved — undo restores exactly these rows.
  const [undoable, setUndoable] = useState<MergeReceipt | null>(null);
  const preview = useMergePreview(loserId || null);
  const merge = useMergeProperties();
  const undo = useUndoMerge();

  const valid = keeperId && loserId && keeperId !== loserId;
  const keeper = props.find((p) => p.id === keeperId);
  const loser = props.find((p) => p.id === loserId);

  return (
    <div className="mt-4 space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Keep this property">
          <select value={keeperId} disabled={disabled} onChange={(e) => { setKeeperId(e.target.value); setConfirming(false); }} className="adminp w-full">
            <option value="">Select…</option>
            {props.filter((p) => p.id !== loserId).map((p) => <option key={p.id} value={p.id}>{label(p)}</option>)}
          </select>
        </Field>
        <Field label="Merge this duplicate in">
          <select value={loserId} disabled={disabled} onChange={(e) => { setLoserId(e.target.value); setConfirming(false); }} className="adminp w-full">
            <option value="">Select…</option>
            {props.filter((p) => p.id !== keeperId).map((p) => <option key={p.id} value={p.id}>{label(p)}</option>)}
          </select>
        </Field>
      </div>

      {valid && preview.data && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2.5 text-xs leading-relaxed text-amber-900">
          <div className="flex items-center gap-1.5 font-semibold">
            <AlertTriangle className="h-3.5 w-3.5" /> Preview
          </div>
          <p className="mt-1">
            <span className="font-medium">{loser && label(loser)}</span> folds into{" "}
            <span className="font-medium">{keeper && label(keeper)}</span>. Moving{" "}
            {preview.data.own} ownership row{preview.data.own === 1 ? "" : "s"}, {preview.data.equip} equipment record{preview.data.equip === 1 ? "" : "s"}, and{" "}
            {preview.data.jobs} job{preview.data.jobs === 1 ? "" : "s"} to the keeper. The duplicate is hidden, not deleted — reversible.
          </p>
        </div>
      )}

      <div className="flex items-center gap-2">
        {!confirming ? (
          <Button size="sm" disabled={disabled || !valid} onClick={() => setConfirming(true)}>
            <GitMerge className="h-3.5 w-3.5" /> Merge…
          </Button>
        ) : (
          <>
            <Button size="sm" disabled={merge.isPending} onClick={() =>
              merge.mutate({ keeperId, loserId }, {
                onSuccess: (receipt) => { setUndoable(receipt); setConfirming(false); setLoserId(""); },
              })
            }>
              <Check className="h-3.5 w-3.5" /> {merge.isPending ? "Merging…" : "Confirm merge"}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>Cancel</Button>
          </>
        )}
        {undoable && (
          <button type="button" disabled={undo.isPending} onClick={() => undo.mutate(undoable, { onSuccess: () => setUndoable(null) })}
            className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700 disabled:opacity-50">
            <Undo2 className="h-3.5 w-3.5" /> {undo.isPending ? "Undoing…" : "Undo last merge (full restore)"}
          </button>
        )}
      </div>
      {merge.isError && (
        <p className="text-xs font-medium text-rose-600">{(merge.error as Error)?.message ?? "The merge failed — nothing may have moved. Try again."}</p>
      )}
      {undo.isError && (
        <p className="text-xs font-medium text-rose-600">{(undo.error as Error)?.message ?? "Undo failed — try again."}</p>
      )}
      {undoable && !undo.isPending && (
        <p className="text-[11px] text-slate-400">Undo is available until you leave this page — it puts back exactly what this merge moved.</p>
      )}
      <style>{`.adminp{border:1px solid var(--hairline,#e2e8f0);border-radius:0.375rem;padding:0.45rem 0.55rem;font-size:0.8125rem;font-family:inherit;background:white}.adminp:disabled{background:#f8fafc;color:#94a3b8}`}</style>
    </div>
  );
}

function MovePanel({ props, disabled }: { props: AdminProperty[]; disabled: boolean }) {
  const [id, setId] = useState("");
  const [custno, setCustno] = useState("");
  const [picked, setPicked] = useState<PickedCustomer | null>(null);
  const [locno, setLocno] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [doneFlash, setDoneFlash] = useState(false);
  const move = useMoveProperty();
  const prop = props.find((p) => p.id === id);
  const valid = id && custno.trim().length > 0;

  return (
    <div className="mt-4 space-y-3">
      <Field label="Property to move">
        <select value={id} disabled={disabled} onChange={(e) => { setId(e.target.value); setConfirming(false); }} className="adminp w-full">
          <option value="">Select…</option>
          {props.map((p) => <option key={p.id} value={p.id}>{label(p)}</option>)}
        </select>
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Move to this customer">
          {/* SEARCH-FIRST (Seth 2026-08-20): find the receiving customer by
              anything - no memorized ESC numbers. */}
          {disabled ? (
            <input disabled className="adminp w-full" placeholder="Read-only" />
          ) : (
            <CustomerPicker
              selected={picked}
              onClear={() => { setPicked(null); setCustno(""); setConfirming(false); }}
              onSelect={(c) => { setPicked(c); setCustno(c.custno); setConfirming(false); }}
              placeholder="Search name, address, phone, or #"
            />
          )}
        </Field>
        <Field label="Location # (optional)">
          <input value={locno} disabled={disabled} onChange={(e) => { setLocno(e.target.value); setConfirming(false); }} placeholder="blank = main (00001)" className="adminp w-full" />
        </Field>
      </div>

      {valid && prop && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs leading-relaxed text-slate-600">
          <span className="font-medium">{label(prop)}</span> moves to{" "}
          <span className="font-medium">{picked ? picked.name : custno.trim()} ({custno.trim()}{locno.trim() ? `/${locno.trim()}` : ""})</span>. Its equipment and history travel with it.
        </div>
      )}

      <div className="flex items-center gap-2">
        {!confirming ? (
          <Button size="sm" disabled={disabled || !valid} onClick={() => setConfirming(true)}>
            <ArrowRightLeft className="h-3.5 w-3.5" /> Move…
          </Button>
        ) : (
          <>
            <Button size="sm" disabled={move.isPending} onClick={() =>
              move.mutate({ id, custno, locno: locno || null }, {
                onSuccess: () => { setConfirming(false); setDoneFlash(true); window.setTimeout(() => setDoneFlash(false), 2500); },
              })
            }>
              <Check className="h-3.5 w-3.5" /> {move.isPending ? "Moving…" : "Confirm move"}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>Cancel</Button>
          </>
        )}
        {doneFlash && <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">moved ✓</span>}
      </div>
      <style>{`.adminp{border:1px solid var(--hairline,#e2e8f0);border-radius:0.375rem;padding:0.45rem 0.55rem;font-size:0.8125rem;font-family:inherit;background:white}.adminp:disabled{background:#f8fafc;color:#94a3b8}`}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-slate-600">{label}</span>
      {children}
    </label>
  );
}
